/**
 * Keep the app working while the browser translates the page.
 *
 * Chrome/Edge translation replaces text nodes with
 * `<font dir="auto" style="vertical-align: inherit;">` wrappers. Vue still holds
 * the original nodes, so its next patch calls `insertBefore` / `removeChild`
 * with a node that is no longer a child of that parent, and the browser throws
 * "NotFoundError: Failed to execute 'insertBefore' on 'Node'".
 *
 * Instead of blocking translation, these two methods tolerate that one case:
 * a reference/child that is no longer under this parent is handled the way the
 * DOM would if it were simply missing (append / skip), with a console warning.
 * Every other call is passed straight through, unchanged.
 */
export const installTranslationSafeDom = (): void => {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("removeChild skipped: node is no longer a child (page translation?)", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn("insertBefore fell back to append: reference node moved (page translation?)", referenceNode);
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
